import { CommonState, MultiProductState } from 'embed/reducers'
import {
  AD_DISCLOSURE_COPY,
  ApplicationType,
  Brand,
  ClientTags,
  Config,
  ConfigServiceDataKeys,
  CreditCardFlow,
  CreditCardStep,
  DepositsFlow,
  FeatureFlag,
  IntegrationStatusValues,
  LoanFlow,
  LoanPurposeOptions,
  LoanSortOption,
  LoanStep,
  Product,
  ProductStep,
  QueryParamKeys,
  TrustedPartnersSectionLocation,
} from '@evenfinancial/even-ts-static'

import {
  AppPrefixQueryParams,
  DefaultCreditCardFlow,
  DEPOSITS_AD_DISCLOSURE_COPY,
  EMPOWER_PROD_COMPANY_UUID,
  OfferOverflowList,
} from 'embed/constants'
import { getBool } from '../utils'
import { DeepReadonly } from 'utility-types'

export const configStateSelector = (state: CommonState): DeepReadonly<Config> => state.config
export const configCompanyUuidSelector = (state: CommonState): string | undefined =>
  configStateSelector(state).company_uuid

export const configIsConsumerSelector = (state: CommonState): boolean => getBool(configStateSelector(state)['consumer'])

export const configOfferOverflowSelector = (state: CommonState): OfferOverflowList | undefined =>
  configStateSelector(state)[QueryParamKeys.OfferOverflow] as OfferOverflowList

export const configProductSelector = (state: CommonState): Product | undefined => configStateSelector(state).product

export const configApplicationFlowSelector = (state: CommonState): LoanFlow | undefined =>
  configStateSelector(state).loan_flow
export const creditFlowSelector = (state: CommonState): CreditCardFlow => {
  const flow = configStateSelector(state).credit_card_flow
  return flow && Object.values(CreditCardFlow).includes(flow) ? flow : DefaultCreditCardFlow
}

export const configCreditCardApplicationTypeSelector = (state: CommonState): ApplicationType | undefined =>
  configStateSelector(state).credit_card_application_type
export const configAvailableProductsSelector = (state: CommonState): DeepReadonly<Product[]> | undefined =>
  configStateSelector(state).available_products
export const configPartnerDisclaimerSelector = (state: CommonState): string | undefined =>
  configStateSelector(state).partnerDisclaimer
export const configStepSelector = (state: CommonState): ProductStep | undefined => configStateSelector(state).step

export const configShowTrustedPartners = (state: CommonState): TrustedPartnersSectionLocation =>
  (configStateSelector(state)[ConfigServiceDataKeys.showTrustedPartners] as TrustedPartnersSectionLocation) ??
  TrustedPartnersSectionLocation.None
export const clientTagsSelector = (state: CommonState): DeepReadonly<ClientTags> | undefined =>
  configStateSelector(state).clientTags
export const configResultsUuidSelector = (state: CommonState): string | undefined => configStateSelector(state).results
export const configShouldAutoRedirectSelector = (state: CommonState): boolean | undefined =>
  configStateSelector(state).shouldAutoRedirect
export const depositsFlowSelector = (state: CommonState): DepositsFlow | undefined =>
  configStateSelector(state).deposits_flow
export const configCreditCardRewardsEnabledSelector = (state: CommonState): boolean =>
  getBool(configStateSelector(state)[ConfigServiceDataKeys.creditCardRewardsEnabled])
export const configShouldAllowCoApplicantSelector = (state: CommonState): boolean =>
  getBool(configStateSelector(state)[ConfigServiceDataKeys.shouldAllowLoanCoApplicant])
export const brandSelector = (state: CommonState): Brand => {
  const config = configStateSelector(state)
  return config.brand ?? (config.pw_fiona ? Brand.Fiona : Brand.Even)
}

const configIntegrationstatusSelector = (state: CommonState): IntegrationStatusValues | undefined =>
  configStateSelector(state)[ConfigServiceDataKeys.integrationStatus]

export const isDeactivatedSelector = (state: CommonState): boolean => {
  const integrationStatus = configIntegrationstatusSelector(state)
  return integrationStatus === IntegrationStatusValues.Deactivated
}

export const isFionaBrandedSelector = (state: CommonState): boolean => brandSelector(state) === Brand.Fiona

export const isPortalPreviewSelector = (state: CommonState): boolean =>
  getBool(configStateSelector(state)[QueryParamKeys.IsPreview])

export const isFionaAccountCreationEnabled = (state: CommonState): boolean => {
  const config = configStateSelector(state)
  const shouldAllowFionaAccount = getBool(config[ConfigServiceDataKeys.shouldAllowFionaAccount])
  return isFionaBrandedSelector(state) && shouldAllowFionaAccount
}

export const configIncentivesEnabledSelector = (state: CommonState): boolean =>
  getBool(configStateSelector(state)[ConfigServiceDataKeys.incentives_enabled])

export const configShouldDisplayBackToSearchSelector = (state: CommonState) => {
  switch (configStepSelector(state)) {
    case CreditCardStep.PreApprovalForm:
    case CreditCardStep.PreQualifiedForm:
    case CreditCardStep.PreSelectForm:
      return false
    default:
      return true
  }
}

export const configRatesSortOptionSelector = (state: CommonState): LoanSortOption | undefined => {
  const defaultLoanSort = configStateSelector(state)['loan_sort_option']
  return Object.values(LoanSortOption).includes(defaultLoanSort) ? defaultLoanSort : undefined
}

export const shouldEnableGeoLocationSelector = (state: MultiProductState) => {
  const selectedProduct = state.ui?.selectedProduct
  switch (selectedProduct) {
    case Product.CreditCard:
      return false
    case Product.Loan:
      const initialStep = configStepSelector(state)
      switch (initialStep) {
        case LoanStep.Application:
        case LoanStep.Results:
          return false
        default:
          return true
      }
    default:
      return true
  }
}

export const isDotDashSelector = (state: CommonState): boolean =>
  [
    '25fc330c-a740-4978-9f2e-eb1705000029',
    'f4dbaaf7-76c8-4e2b-b8e2-94a09d7638b1',
    '2a702bfa-c31b-4330-8ded-b7c8ee5e031e',
    'beee3060-fdf7-40d0-a491-b67f31f43e10',
  ].includes(configStateSelector(state).company_uuid ?? '')

export const configAllowPersonalizedLinkoutSelector = (state: CommonState): boolean =>
  getBool(configStateSelector(state).allowPersonalizedLinkout)

export const configFeatureFlagsSelector = (state: MultiProductState): FeatureFlag[] => {
  const featureFlags = (configStateSelector(state) ?? {}).featureFlags ?? ''
  return featureFlags.split(',') as FeatureFlag[]
}

export const configFloatingLabelInputsSelector = (state: MultiProductState): boolean =>
  getBool(configStateSelector(state)[ConfigServiceDataKeys.themeFloatingLabelInputs])

export const configAppPurposeSelector = (state: MultiProductState): LoanPurposeOptions | undefined =>
  configStateSelector(state)[AppPrefixQueryParams.AppPurpose]

export const configAdvertiserDisclosureCopySelector = (state: MultiProductState): string => {
  const selectedProduct = state.ui?.selectedProduct
  const adDisclosureDefaultCopy =
    selectedProduct === Product.Deposits ? DEPOSITS_AD_DISCLOSURE_COPY : AD_DISCLOSURE_COPY
  return configStateSelector(state).advertiserDisclosureCopy ?? adDisclosureDefaultCopy
}

export const configShouldUseCustomBackgroundSelector = (state: MultiProductState): boolean =>
  (configFeatureFlagsSelector(state) ?? []).includes(FeatureFlag.EnableEmbedCustomBackground)

export const configFixedWidthSelector = (state: MultiProductState): boolean =>
  configShouldUseCustomBackgroundSelector(state) &&
  getBool(configStateSelector(state)[ConfigServiceDataKeys.themeFixedAppWidth])
